import { supabase } from "../lib/supabase";

export class RemoteSupportService {
  // Helper function to format currency for display
  static formatCurrency(amount) {
    if (amount == null) return null;
    return parseFloat(amount).toFixed(2);
  }

  static async getAllClients() {
    try {
      const { data, error } = await supabase
        .from("remote_support_clients")
        .select("*")
        .order("company_name");

      if (error) {
        throw new Error(`Failed to fetch remote support clients: ${error.message}`);
      }

      const processedData = data.map((client) => ({
        id: client.id,
        companyName: client.company_name,
        dataCollectors: client.data_collectors || "",
        invoiceDescription: client.invoice_description || "",
        baseCost: this.formatCurrency(client.base_cost),
        additionalCost: this.formatCurrency(client.additional_cost),
        totalCost: this.formatCurrency(client.total_cost),
        notes: client.notes || "",
        createdAt: client.created_at,
        updatedAt: client.updated_at,
      }));

      return processedData;
    } catch (error) {
      console.error("Error fetching remote support clients:", error);
      throw error;
    }
  }

  static async getPaginatedClients(page = 1, pageSize = 50, sortField = "company_name", sortDirection = "asc", searchQuery = "") {
    try {
      const offset = (page - 1) * pageSize;

      // Build the query with optional search
      let countQuery = supabase.from("remote_support_clients").select("*", { count: "exact", head: true });
      let dataQuery = supabase.from("remote_support_clients").select("*");

      // Apply search filters if search query is provided
      if (searchQuery && searchQuery.trim()) {
        const query = searchQuery.trim();

        // Apply search to count query
        countQuery = countQuery.or(
          `company_name.ilike.%${query}%,data_collectors.ilike.%${query}%,invoice_description.ilike.%${query}%,notes.ilike.%${query}%`
        );

        // Apply search to data query
        dataQuery = dataQuery.or(
          `company_name.ilike.%${query}%,data_collectors.ilike.%${query}%,invoice_description.ilike.%${query}%,notes.ilike.%${query}%`
        );
      }

      // Get total count for pagination info
      const { count, error: countError } = await countQuery;

      if (countError) {
        console.error('Count query error:', countError);
        throw new Error(`Failed to get remote support clients count: ${countError.message || countError.toString() || 'Unknown error'}`);
      }

      // Handle sorting - map UI field names to database field names
      let actualSortField = sortField;
      const fieldMapping = {
        "companyName": "company_name",
        "dataCollectors": "data_collectors",
        "invoiceDescription": "invoice_description",
        "baseCost": "base_cost",
        "additionalCost": "additional_cost",
        "totalCost": "total_cost"
      };

      // Apply field mapping if it exists
      if (fieldMapping[sortField]) {
        actualSortField = fieldMapping[sortField];
      }

      // Get paginated data with sorting and range
      const { data, error } = await dataQuery
        .order(actualSortField, { ascending: sortDirection === "asc" })
        .range(offset, offset + pageSize - 1);

      if (error) {
        console.error('Data query error:', error);
        throw new Error(`Failed to fetch remote support clients: ${error.message || error.toString() || 'Unknown error'}`);
      }

      const processedData = data.map((client) => ({
        id: client.id,
        companyName: client.company_name,
        dataCollectors: client.data_collectors || "",
        invoiceDescription: client.invoice_description || "",
        baseCost: this.formatCurrency(client.base_cost),
        additionalCost: this.formatCurrency(client.additional_cost),
        totalCost: this.formatCurrency(client.total_cost),
        notes: client.notes || "",
        createdAt: client.created_at,
        updatedAt: client.updated_at,
      }));

      return {
        data: processedData,
        totalCount: count,
        totalPages: Math.ceil(count / pageSize),
        currentPage: page,
        pageSize: pageSize,
        hasNextPage: page < Math.ceil(count / pageSize),
        hasPreviousPage: page > 1
      };
    } catch (error) {
      console.error("Error fetching paginated remote support clients:", error);
      throw error;
    }
  }

  static async updateClient(clientId, fieldOrData, value) {
    try {
      let updateData;

      // Handle both single field update and full object update
      if (typeof fieldOrData === 'string') {
        // Single field update
        const dbField = this.mapFieldToDatabase(fieldOrData);
        updateData = { [dbField]: value };
      } else {
        // Full object update - calculate total cost
        const baseCost = parseFloat(fieldOrData.baseCost) || 0;
        const additionalCost = parseFloat(fieldOrData.additionalCost) || 0;
        const totalCost = baseCost + additionalCost;

        updateData = {
          company_name: fieldOrData.companyName,
          data_collectors: fieldOrData.dataCollectors || null,
          invoice_description: fieldOrData.invoiceDescription || null,
          base_cost: fieldOrData.baseCost ? parseFloat(fieldOrData.baseCost) : null,
          additional_cost: fieldOrData.additionalCost ? parseFloat(fieldOrData.additionalCost) : null,
          total_cost: totalCost || null,
          notes: fieldOrData.notes || null,
          updated_at: new Date().toISOString(),
        };
      }

      // Perform the update without select to work around RLS restrictions
      const { error: updateError } = await supabase
        .from("remote_support_clients")
        .update(updateData)
        .eq("id", clientId);

      if (updateError) {
        console.error('Update error:', updateError);
        throw new Error(`Failed to update remote support client: ${updateError.message}`);
      }

      // Wait a moment to ensure database has processed the update
      await new Promise(resolve => setTimeout(resolve, 100));

      // Fetch the updated client separately (this works with RLS)
      const { data: updatedClient, error: selectError } = await supabase
        .from("remote_support_clients")
        .select("*")
        .eq("id", clientId)
        .single();

      if (selectError) {
        throw new Error(`Failed to fetch updated remote support client: ${selectError.message}`);
      }

      if (!updatedClient) {
        throw new Error('Remote support client was updated but could not be retrieved');
      }

      return {
        id: updatedClient.id,
        companyName: updatedClient.company_name,
        dataCollectors: updatedClient.data_collectors || "",
        invoiceDescription: updatedClient.invoice_description || "",
        baseCost: this.formatCurrency(updatedClient.base_cost),
        additionalCost: this.formatCurrency(updatedClient.additional_cost),
        totalCost: this.formatCurrency(updatedClient.total_cost),
        notes: updatedClient.notes || "",
        createdAt: updatedClient.created_at,
        updatedAt: updatedClient.updated_at,
      };
    } catch (error) {
      console.error("Error updating remote support client:", error);
      throw error;
    }
  }

  static async deleteClient(clientId) {
    try {
      const { error } = await supabase
        .from("remote_support_clients")
        .delete()
        .eq("id", clientId);

      if (error) {
        throw new Error(`Failed to delete remote support client: ${error.message}`);
      }

      return true;
    } catch (error) {
      console.error("Error deleting remote support client:", error);
      throw error;
    }
  }

  static mapFieldToDatabase(field) {
    const fieldMap = {
      companyName: "company_name",
      dataCollectors: "data_collectors",
      invoiceDescription: "invoice_description",
      baseCost: "base_cost",
      additionalCost: "additional_cost",
      totalCost: "total_cost",
      notes: "notes",
    };
    return fieldMap[field] || field;
  }

  static async createClient(clientData) {
    try {
      // Calculate total cost
      const baseCost = parseFloat(clientData.baseCost) || 0;
      const additionalCost = parseFloat(clientData.additionalCost) || 0;
      const totalCost = baseCost + additionalCost;

      const { data, error } = await supabase
        .from("remote_support_clients")
        .insert([
          {
            company_name: clientData.companyName,
            data_collectors: clientData.dataCollectors || null,
            invoice_description: clientData.invoiceDescription || null,
            base_cost: clientData.baseCost ? parseFloat(clientData.baseCost) : null,
            additional_cost: clientData.additionalCost ? parseFloat(clientData.additionalCost) : null,
            total_cost: totalCost || null,
            notes: clientData.notes || null,
          },
        ])
        .select("*");

      if (error) {
        throw new Error(`Failed to create remote support client: ${error.message}`);
      }

      if (!data || data.length === 0) {
        throw new Error('Failed to create remote support client - no data returned');
      }

      const newClient = data[0];
      return {
        id: newClient.id,
        companyName: newClient.company_name,
        dataCollectors: newClient.data_collectors || "",
        invoiceDescription: newClient.invoice_description || "",
        baseCost: this.formatCurrency(newClient.base_cost),
        additionalCost: this.formatCurrency(newClient.additional_cost),
        totalCost: this.formatCurrency(newClient.total_cost),
        notes: newClient.notes || "",
        createdAt: newClient.created_at,
        updatedAt: newClient.updated_at,
      };
    } catch (error) {
      console.error("Error creating remote support client:", error);
      throw error;
    }
  }
}
